import React, { useState, useEffect, useRef } from "react";
import Cookies from "js-cookie";
import useDecodedToken from "../../utils/useDecodedToken";
import "@fortawesome/fontawesome-free/css/all.min.css";
import { useNavigate } from "react-router-dom";
import useTokenAndProfile from "../hooks/FetchUserProfile";
import { fetchSoloDuoInvitations } from "../../api/api";

// Private/app navbar: logo + profile menu only. Section navigation now lives in the Sidebar.
const PrivateNavbar = ({ onToggleSidebar }) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const { decodedJwt } = useDecodedToken();
  const battleNetTag = decodedJwt?.battleNetTag
    ? decodedJwt.battleNetTag.split("#")[0]
    : "Characters";
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const [mainCharacter] = useState({});
  const { token } = useTokenAndProfile();
  const [pendingInvitationsCount, setPendingInvitationsCount] = useState(0);

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    fetchSoloDuoInvitations(token)
      .then((data) => {
        if (cancelled) return;
        const list = Array.isArray(data) ? data : data?.invitations || [];
        const pending = list.filter((inv) => inv.status === "pending");
        setPendingInvitationsCount(pending.length);
      })
      .catch(() => {
        if (!cancelled) setPendingInvitationsCount(0);
      });
    return () => { cancelled = true; };
  }, [token]);

  const toggleDropdown = () => {
    setDropdownOpen((v) => !v);
  };

  const handleClickOutside = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setDropdownOpen(false);
    }
  };

  const handleLogout = () => {
    Cookies.remove("jwt");
    window.location.href = "/";
  };

  const handleNavigateHome = () => {
    navigate("/");
  };

  return (
    <nav className="bg-gradient-to-r from-gray-400 via-gray-700 to-gray-400 w-full shadow-lg border-b border-gray-700">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Sidebar toggle - mobile only */}
          <button
            onClick={onToggleSidebar}
            className="lg:hidden inline-flex items-center justify-center p-2 mr-2 rounded-md text-white bg-gray-700/60 hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
          >
            <i className="fas fa-bars text-lg leading-none"></i>
          </button>

          {/* Logo Section - Left */}
          <div
            className="flex-shrink-0 flex items-center cursor-pointer group"
            onClick={handleNavigateHome}
          >
            <div className="mr-3">
              <img src="/new-logo-terra.png" alt="Terra Logo" className="h-10 w-10 drop-shadow-lg" />
            </div>
            <span className="text-white text-3xl font-bold font-serif tracking-wider group-hover:text-blue-200 transition-colors duration-300">
              TERRA
            </span>
          </div>

          <div className="flex-1" />

          {/* User Profile Section - Right */}
          <div className="flex items-center flex-shrink-0">
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={toggleDropdown}
                className="flex items-center space-x-3 text-gray-300 hover:text-white px-4 py-2 rounded-lg hover:bg-gray-700/50 transition-all duration-300"
              >
                {mainCharacter.mediaLinks &&
                mainCharacter.mediaLinks.length > 0 ? (
                  <img
                    src={mainCharacter.mediaLinks[0].value}
                    alt={mainCharacter.name}
                    className="h-8 w-8 rounded-full ring-2 ring-blue-500"
                  />
                ) : (
                  <div className="h-8 w-8 rounded-full bg-blue-500 flex items-center justify-center">
                    <i className="fas fa-user text-white"></i>
                  </div>
                )}
                <span className="font-medium hidden sm:inline">
                  {mainCharacter.name || battleNetTag}
                </span>
                <i
                  className={`fas fa-chevron-${
                    dropdownOpen ? "up" : "down"
                  } text-sm transition-transform duration-300`}
                ></i>
              </button>

              {dropdownOpen && (
                <div className="absolute right-0 mt-2 w-48 rounded-md shadow-lg py-1 bg-gray-800 ring-1 ring-black ring-opacity-5 transform opacity-100 scale-100 transition-all duration-200 z-50">
                  <a
                    href="/account"
                    className="w-full text-left px-4 py-2 text-sm text-gray-300 hover:text-white hover:bg-gray-700 flex items-center space-x-2"
                    onClick={() => setDropdownOpen(false)}
                  >
                    <i className="fas fa-user"></i>
                    <span>Characters</span>
                  </a>
                  <a
                    href="/solo-duo-ranking/invitations"
                    className="w-full text-left px-4 py-2 text-sm text-gray-300 hover:text-white hover:bg-gray-700 flex items-center space-x-2"
                    onClick={(e) => {
                      e.preventDefault();
                      navigate("/solo-duo-ranking/invitations");
                      setDropdownOpen(false);
                    }}
                  >
                    <i className="fas fa-envelope"></i>
                    <span>Invitations</span>
                    {pendingInvitationsCount > 0 && (
                      <span className="ml-auto min-w-[1.25rem] h-5 px-1.5 rounded-full bg-blue-500 text-white text-xs font-semibold flex items-center justify-center">
                        {pendingInvitationsCount > 99 ? "99+" : pendingInvitationsCount}
                      </span>
                    )}
                  </a>
                  <button
                    onClick={handleLogout}
                    className="w-full text-left px-4 py-2 text-sm text-gray-300 hover:text-white hover:bg-gray-700 flex items-center space-x-2"
                  >
                    <i className="fas fa-sign-out-alt"></i>
                    <span>Logout</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default PrivateNavbar;
